import { FC, useMemo, useState } from 'react';

import { ChannelBalanceSummary } from '@shared/api/giftChannelLedger/giftChannelLedger.interface';
import {
  DELIVERY_CHANNEL_CONFIG,
  DeliveryChannel,
  PendingChargeEstimateItem,
  PendingChargeEstimateResponse,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { Link } from 'react-router-dom';

import { ChannelBalanceChangesModal } from './channelLedger/ChannelBalanceChangesModal';

/**
 * 가격 신뢰도 힌트.
 * - 최근 발송가라도 오래됐으면(8일+) 도매가 변동 가능성을 관리자가 감안하도록 표기
 * - 발송 이력이 없어 카탈로그 단가로 폴백한 경우/단가 미상도 구분 표기
 */
function estimateHint(item: PendingChargeEstimateItem): string | null {
  if (item.priceSource === 'recentDelivered') {
    if (!item.refDeliveredAt) return null;
    const days = Math.floor(
      (Date.now() - new Date(item.refDeliveredAt).getTime()) / 86_400_000,
    );
    return days >= 8 ? `${days}일 전 가격` : null;
  }
  if (item.priceSource === 'none') return '단가 미상';
  return '카탈로그 단가';
}

/**
 * 발송대기 매체별 충전 필요액 예상 칩 — 칩 클릭 시 상품별 내역 팝오버.
 * chargeEstimate가 null이면(구서버 404 등) 예상 칩은 숨기고,
 * balances(충전금 미러 원장 잔액)가 오면 우측에 현재 잔액을 함께 표기한다.
 */
export const ChargeEstimateChips: FC<{
  estimate: PendingChargeEstimateResponse | null;
  balances?: ChannelBalanceSummary[] | null;
}> = ({ estimate, balances }) => {
  const [openKey, setOpenKey] = useState<string | null>(null);
  // 잔액 칩 클릭 시 — 어제 12:00 이후 잔액 변경 내역 모달
  const [balanceModalChannel, setBalanceModalChannel] = useState<DeliveryChannel | null>(
    null,
  );

  const balanceByChannel = useMemo(
    () => new Map((balances ?? []).map((b) => [b.channel, b.balance])),
    [balances],
  );
  const estimateChannels = estimate?.channels ?? [];
  const openBucket =
    estimateChannels.find((b) => (b.channel ?? 'null') === openKey) ?? null;
  const openLabel = openBucket
    ? openBucket.channel
      ? DELIVERY_CHANNEL_CONFIG[openBucket.channel].label
      : '미분류'
    : '';

  return (
    <div className="relative ml-auto flex flex-wrap items-center justify-end gap-1.5">
      {estimateChannels.length > 0 && (
        <span
          className="text-xs text-gray-400"
          title="발송대기 전량 발송 시 매체별 예상 구매액 — 같은 상품의 최근 발송가 기준"
        >
          예상 발송대기 금액
        </span>
      )}

      {estimateChannels.map((bucket) => {
        const key = bucket.channel ?? 'null';
        const config = bucket.channel ? DELIVERY_CHANNEL_CONFIG[bucket.channel] : null;
        const isOpen = openKey === key;
        const balance = bucket.channel ? balanceByChannel.get(bucket.channel) : undefined;
        const isShort = balance !== undefined && balance < bucket.estimatedAmount;
        return (
          <button
            key={key}
            type="button"
            onClick={() => setOpenKey(isOpen ? null : key)}
            title={`${bucket.pendingCount}건 — 클릭하여 상품별 내역 보기`}
            className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${
              config ? config.badgeClass : 'border-gray-300 bg-gray-50 text-gray-600'
            } ${isOpen ? 'ring-2 ring-indigo-300' : ''}`}
          >
            {config?.label ?? `미분류 ${bucket.pendingCount}건`}{' '}
            {bucket.estimatedAmount.toLocaleString()}원
            {isShort && <span className="ml-1 font-bold text-red-600">부족</span>}
          </button>
        );
      })}

      {balances && balances.length > 0 && (
        <>
          {estimateChannels.length > 0 && <span className="mx-0.5 text-gray-300">|</span>}
          <span
            className="text-xs text-gray-400"
            title="매체 충전금 미러 잔액 — 칩을 클릭하면 어제 12:00 이후 변경 내역"
          >
            현재 잔액
          </span>
          {balances.map((summary) => {
            const config = DELIVERY_CHANNEL_CONFIG[summary.channel];
            return (
              <button
                key={summary.channel}
                type="button"
                onClick={() => setBalanceModalChannel(summary.channel)}
                title="어제 12:00 이후 잔액 변경 내역 보기"
                className={`rounded-full border px-2.5 py-0.5 text-xs font-medium hover:opacity-75 ${config.badgeClass}`}
              >
                {config.label}{' '}
                <span className={summary.balance < 0 ? 'font-bold text-red-600' : ''}>
                  {summary.balance.toLocaleString()}원
                </span>
              </button>
            );
          })}
        </>
      )}

      {balanceModalChannel && (
        <ChannelBalanceChangesModal
          initialChannel={balanceModalChannel}
          onClose={() => setBalanceModalChannel(null)}
        />
      )}

      {openBucket && (
        <div className="absolute right-0 top-full z-20 mt-1.5 w-[360px] rounded-lg border bg-white text-left shadow-lg">
          <div className="border-b px-3 py-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-700">
                {openLabel} — {openBucket.pendingCount}건 ·{' '}
                {openBucket.estimatedAmount.toLocaleString()}원
              </span>
              <button
                type="button"
                onClick={() => setOpenKey(null)}
                className="rounded px-1 text-xs text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            {(() => {
              // 미러 잔액 대비 발송 후 잔액 — 부족이면 충전 필요액을 강조
              const balance = openBucket.channel
                ? balanceByChannel.get(openBucket.channel)
                : undefined;
              if (balance === undefined) return null;
              const afterBalance = balance - openBucket.estimatedAmount;
              return (
                <div className="mt-1 flex items-center justify-between">
                  <span
                    className={`text-[11px] ${
                      afterBalance < 0 ? 'font-bold text-red-600' : 'text-gray-500'
                    }`}
                  >
                    미러 잔액 {balance.toLocaleString()}원 → 발송 후{' '}
                    {afterBalance.toLocaleString()}원
                    {afterBalance < 0 &&
                      ` (충전 필요 ${(-afterBalance).toLocaleString()}원)`}
                  </span>
                  <Link
                    to="/gift-seller/channel-ledger"
                    className="shrink-0 text-[11px] text-indigo-500 hover:underline"
                  >
                    충전금 원장 →
                  </Link>
                </div>
              );
            })()}
          </div>
          <div className="max-h-[240px] divide-y overflow-y-auto">
            {openBucket.items.map((item) => {
              const hint = estimateHint(item);
              return (
                <div
                  key={item.key}
                  className="flex items-center justify-between gap-2 px-3 py-1.5"
                >
                  <div className="min-w-0">
                    <p className="truncate text-xs text-gray-800" title={item.name}>
                      {item.name}
                    </p>
                    {hint && (
                      <p
                        className={`text-[10px] ${
                          item.priceSource === 'none' ? 'text-red-500' : 'text-amber-600'
                        }`}
                      >
                        {hint}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-xs font-medium text-gray-900">
                      {(item.count * item.unitPriceWhole).toLocaleString()}원
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {item.count}건 × {item.unitPriceWhole.toLocaleString()}원
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
